/**
 * Project filter interface for type-safe query keys
 * 
 * @property archived - Filter by archived status
 * @property userId - Filter by user ID (owner)
 * @property search - Filter by search term
 * @property limit - Limit the number of results
 * @property sortBy - Field to sort by
 * @property sortDirection - Direction to sort (asc/desc)
 */
export interface ProjectFilters {
  archived?: boolean;
  userId?: string;
  search?: string;
  limit?: number;
  sortBy?: 'name' | 'lastUpdated' | 'lastSelected';
  sortDirection?: 'asc' | 'desc';
}

/**
 * Query key factory for projects
 * 
 * These keys are used to identify and organize React Query cache entries
 * related to projects. Following TanStack Query best practices for structured keys.
 * 
 * Query keys are organized hierarchically to allow for selective invalidation:
 * - projectKeys.all: Invalidates all project-related queries
 * - projectKeys.lists(): Invalidates all project list queries
 * - projectKeys.list(filters): Invalidates specific filtered lists
 * - projectKeys.details(): Invalidates all individual project queries
 * - projectKeys.detail(id): Invalidates a specific project
 * - projectKeys.sharedStatus(): Invalidates all shared status queries
 * 
 * Usage examples:
 * - projectKeys.all - Base key for all project data
 * - projectKeys.lists() - Key for all project lists
 * - projectKeys.list({ archived: true }) - Key for archived projects list
 * - projectKeys.detail('proj-123') - Key for a specific project
 * - projectKeys.sharedStatusForIds(['id1', 'id2']) - Key for shared status of specific projects
 * 
 * @example
 * // Invalidate all project data
 * queryClient.invalidateQueries({ queryKey: projectKeys.all });
 * 
 * @example
 * // Fetch a specific project
 * useQuery({ queryKey: projectKeys.detail('proj-123'), queryFn: fetchProject });
 */
export const projectKeys = {
  /**
   * Base key for all project-related queries
   * Invalidating this key will invalidate all project queries
   */
  all: ['projects'] as const,
  
  /**
   * Keys for all project lists
   * Invalidating this key will invalidate all list queries but not detail queries
   * 
   * @returns Tuple of ['projects', 'list']
   */
  lists: () => [...projectKeys.all, 'list'] as const,
  
  /**
   * Key for filtered project lists
   * Used for storing and retrieving specific filtered views of projects
   * 
   * @param filters - Object containing filter criteria
   * @returns Tuple of ['projects', 'list', {...filters}]
   * 
   * @example
   * // Get active projects
   * projectKeys.list({ archived: false })
   * 
   * @example
   * // Get archived projects for a specific user
   * projectKeys.list({ archived: true, userId: 'user-123' })
   * 
   * @example
   * // Get sorted projects
   * projectKeys.list({ sortBy: 'lastUpdated', sortDirection: 'desc' })
   */
  list: (filters: ProjectFilters = {}) => [...projectKeys.lists(), filters] as const,
  
  /**
   * Keys for all individual project details
   * Invalidating this will invalidate all individual project queries
   * 
   * @returns Tuple of ['projects', 'detail']
   */
  details: () => [...projectKeys.all, 'detail'] as const,
  
  /**
   * Key for a specific project by ID
   * Used for storing and retrieving data about a specific project
   * 
   * @param id - Project ID
   * @returns Tuple of ['projects', 'detail', id]
   * 
   * @example
   * // Get project with ID "proj-123"
   * projectKeys.detail('proj-123')
   * 
   * @example
   * // Invalidate a specific project
   * queryClient.invalidateQueries({ queryKey: projectKeys.detail('proj-123') })
   */
  detail: (id: string) => [...projectKeys.details(), id] as const,
  
  /**
   * Keys for all shared project status queries
   * Invalidating this will invalidate all shared status queries
   * 
   * @returns Tuple of ['projects', 'shared-status']
   */
  sharedStatus: () => [...projectKeys.all, 'shared-status'] as const,
  
  /**
   * Key for batch fetching shared status of multiple projects
   * Used for storing and retrieving information about whether projects are shared
   * 
   * @param ids - Array of project IDs to check for shared status
   * @returns Tuple of ['projects', 'shared-status', {ids: string[]}]
   * 
   * @example
   * // Get shared status for specific project IDs
   * projectKeys.sharedStatusForIds(['proj-123', 'proj-456'])
   * 
   * @example
   * // Fetch shared status
   * useQuery({
   *   queryKey: projectKeys.sharedStatusForIds(projectIds),
   *   queryFn: () => fetchSharedStatus(projectIds)
   * })
   */
  sharedStatusForIds: (ids: string[]) => [...projectKeys.sharedStatus(), { ids }] as const,
};